import { useState } from 'react'
import { lazy } from 'react'
import { CheckBox, FormikError, NVButton, Select, TextField } from '@nv/basic-components'
import { AlertBanner, PhoneInput, useNVModal } from '@nv/components'
import { useConfigurationContext } from '@nv/contexts'
import { AdminVoterService } from '@nv/services'
import { days, months, years } from '@nv/utils'
import { useFormik } from 'formik'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'
import { AddressInput } from '../registration/address/AddressInput'
import { MailingAddressInput } from '../registration/address/MailingAddressInput'
import styles from './AdminAddElector.module.scss'

const NVBasicModal = lazy(() => import('@nv/lazy-loadable/lib/modals/NVBasicModal'))

export const AdminAddElector = () => {
  const { t } = useTranslation()
  const [successVoterId, setSuccessVoterId] = useState('')
  const [serverError, setServerError] = useState('')
  const { NVModal, openModal } = useNVModal()
  const {
    electionDate,
    minAge,
    votingMethodOptions,
    includeSingleNameField,
    knownIdNumberTextFieldConfig,
    mailingAddressRequiredVoteByMail,
    enableDifferentMailingAddress,
    includeKnownIdNumber,
  } = useConfigurationContext()

  const testMultipleRegexes = (regexArr, str) => {
    for (var regex of regexArr) {
      if (regex.test(str)) {
        return true
      }
    }
    return false
  }

  const validationSchema = Yup.object()
    .shape({
      firstName: Yup.string().required(t('admin.users.firstNameRequired')),
      lastName: Yup.string().when('singleName', {
        is: false,
        then: Yup.string().required(t('admin.users.lastNameRequired')),
        otherwise: Yup.string(),
      }),
      email: Yup.string().email(t('admin.users.invalidEmail')).required(t('form.validations.email')),
      confirmEmail: Yup.string()
        .oneOf([Yup.ref('email'), null], t('admin.users.emailsMustMatch'))
        .required(t('admin.users.confirmEmailRequired')),
      dobMonth: Yup.string().required(t('admin.users.monthRequired')),
      dobDay: Yup.string().required(t('admin.users.dayRequired')),
      dobYear: Yup.string().required(t('admin.users.yearRequired')),
      singleName: Yup.boolean(),
      addressNotListed: Yup.boolean(),
      searchAddress: Yup.object().when('addressNotListed', {
        is: false,
        then: () =>
          Yup.object().shape({
            streetAddress: Yup.string().required(t('form.validations.address')),
          }),
        otherwise: () => Yup.object().notRequired(),
      }),
      address: Yup.object().when('addressNotListed', {
        is: true,
        then: () =>
          Yup.object().shape({
            streetAddress: Yup.string().required(t('form.validations.street')),
            unitNumberPOBox: Yup.string().notRequired(),
            city: Yup.string().required(t('form.validations.city')),
            province: Yup.string().required(t('form.validations.province')),
            postalCode: Yup.string().required(t('form.validations.postalCode')),
            country: Yup.string().required(t('form.validations.country')),
          }),
        otherwise: () => Yup.object().notRequired(),
      }),
      mailingAddressNotListed: Yup.boolean(),
      differentMailingAddress: Yup.boolean(),
      searchMailingAddress: Yup.object().when(['differentMailingAddress', 'mailingAddressNotListed', 'votingChannel'], {
        is: (differentMailingAddress, mailingAddressNotList, votingChannel) =>
          (differentMailingAddress && !mailingAddressNotList) ||
          (votingChannel === 'mail' && mailingAddressRequiredVoteByMail && !mailingAddressNotList),
        then: () =>
          Yup.object().shape({
            formattedAddress: Yup.string().required(t('form.validations.address')),
          }),
        otherwise: () => Yup.object().notRequired(),
      }),
      mailingAddress: Yup.object().when(['differentMailingAddress', 'mailingAddressNotListed', 'votingChannel'], {
        is: (differentMailingAddress, mailingAddressNotList, votingChannel) =>
          (differentMailingAddress && mailingAddressNotList) ||
          (votingChannel === 'mail' && mailingAddressRequiredVoteByMail && mailingAddressNotList),
        then: () =>
          Yup.object().shape({
            streetAddress: Yup.string().required(t('form.validations.street')),
            unitNumberPOBox: Yup.string().notRequired(),
            city: Yup.string().required(t('form.validations.city')),
            province: Yup.string().when(['country'], {
              is: (country) =>
                !testMultipleRegexes([/US/i, /USA/i, /UNITED STATES/i, /UNITED STATES OF AMERICA/i, /CA/i, /CANADA/i], country),
              then: () => Yup.string(),
              otherwise: () => Yup.string().required(t('form.validations.province')),
            }),

            postalCode: Yup.string().required(t('form.validations.postalCode')),
          }),
        otherwise: () => Yup.object().notRequired(),
      }),
      electionConfirmations: Yup.object()
        .shape({
          eligiblePSB: Yup.boolean(),
          eligibleCSLF: Yup.boolean(),
        })
        .test('at-least-one-true', t('form.validations.oneConfirmationIsRequired'), function (value) {
          return value.eligiblePSB || value.eligibleCSLF
        }),
      votingChannel: Yup.string().required(t('form.validations.votingMethodRequired')),
    })
    .test('is-valid-date', function (value) {
      const { dobDay, dobMonth, dobYear } = value
      if (!dobDay || !dobMonth || !dobYear) return false
      const day = parseInt(dobDay)
      const month = parseInt(dobMonth)
      const year = parseInt(dobYear)
      const date = new Date(year, month - 1, day)
      const isValidDate = date.getDate() === day && date.getMonth() === month - 1 && date.getFullYear() === year
      return isValidDate || this.createError({ path: 'dobYear', message: t('registration.form.validations.validBirthDate') })
    })
    .test('is-over-18', function (value) {
      const { dobDay, dobMonth, dobYear } = value
      if (!dobDay || !dobMonth || !dobYear) return false
      const day = parseInt(dobDay)
      const month = parseInt(dobMonth)
      const year = parseInt(dobYear)
      const dateCompareBirthday = electionDate ? electionDate : new Date()
      const birthDate = new Date(year, month - 1, day)
      const age = dateCompareBirthday.getFullYear() - birthDate.getFullYear()
      const monthDiff = dateCompareBirthday.getMonth() - birthDate.getMonth()
      const dayDiff = dateCompareBirthday.getDate() - birthDate.getDate()
      const isOverMinAge = age > minAge || (age === minAge && (monthDiff > 0 || (monthDiff === 0 && dayDiff >= 0)))
      return isOverMinAge || this.createError({ path: 'dobYear', message: t('registration.form.validations.minAge', { age: minAge }) })
    })

  const formik = useFormik({
    initialValues: {
      firstName: '',
      middleName: '',
      lastName: '',
      singleName: false,
      dobMonth: '',
      dobDay: '',
      dobYear: '',
      email: '',
      confirmEmail: '',
      address: {
        streetAddress: '',
        unitNumberPOBox: '',
        city: '',
        province: '',
        postalCode: '',
      },
      searchAddress: {
        streetAddress: '',
        unitNumberPOBox: '',
        city: '',
        province: '',
        postalCode: '',
        formattedAddress: '',
        // _id: '',
        value: '',
      },
      mailingAddress: {
        streetAddress: '',
        unitNumberPOBox: '',
        city: '',
        province: '',
        postalCode: '',
        country: '',
      },
      searchMailingAddress: {
        streetAddress: '',
        unitNumberPOBox: '',
        city: '',
        province: '',
        country: '',
        postalCode: '',
        formattedAddress: '',
        value: '',
      },
      electionConfirmations: {
        eligiblePSB: false,
        eligibleCSLF: false,
      },
      votingChannel: '',
      knownIdNumber: '',
      addressNotListed: false,
      mailingAddressNotListed: false,
      differentMailingAddress: false,
    },
    validationSchema,
    onSubmit: async (values) => {
      setServerError('')
      let electionOptIns = ''
      for (var key in values.electionConfirmations) {
        if (values.electionConfirmations[key]) {
          if (electionOptIns.length) {
            electionOptIns += ',' + key
          } else {
            electionOptIns += key
          }
        }
      }

      const data = {
        ...values,
        electionOptIns,
        dateOfBirth: `${values.dobYear}-${values.dobMonth.toString().padStart(2, '0')}-${values.dobDay.toString().padStart(2, '0')}`,
        address: values.addressNotListed ? values.address : values.searchAddress,
      }
      if (values.differentMailingAddress || mailingAddressRequiredVoteByMail) {
        data.mailingAddress = values.mailingAddressNotListed ? values.mailingAddress : values.searchMailingAddress
      }

      const registered = await AdminVoterService.registerVoter(data)
      if (registered.success) {
        openModal()
        setSuccessVoterId(registered.data?.voterIdentifier)
      } else {
        switch (registered.error?.errorCode) {
          case 'REGISTERED':
            setServerError('REGISTERED')
            break
          default:
            setServerError('INELIGIBLE')
            break
        }
      }
    },
  })

  return (
    <>
      <NVModal>
        <NVBasicModal
          title={t('admin.users.addNewElector')}
          text={t('admin.electorsList.newElectorSuccessfullyAdded')}
          success={true}
          navigateTo={`/admin/registrant-details/${successVoterId}`}
        />
      </NVModal>

      <div className={styles['container']}>
        <h1 className={styles['header']}>{t('admin.users.addNewElector')}</h1>
        <form onSubmit={formik.handleSubmit} className={styles['form']}>
          <div className={styles['hr']}>{t('registration.votingMethod')}</div>
          <div className={styles['form__voting-method']}>
            <Select
              label={t('registration.selectVotingChannel')}
              formik={formik}
              options={votingMethodOptions}
              field='votingChannel'
              value={formik.values.votingChannel}
              isRequired={true}
            />
          </div>
          <div className={styles['hr']}>{t('registration.basicInfo')}</div>
          {/* Name Section */}
          <div className={styles['form__name']}>
            <TextField
              formik={formik}
              type='text'
              field='firstName'
              placeholder={t('form.labels.firstName')}
              label={t('form.labels.firstName')}
              isRequired={true}
              autocomplete='off'
            />
            <TextField
              formik={formik}
              type='text'
              field='middleName'
              placeholder={t('form.labels.middleName')}
              label={t('form.labels.middleName')}
              isRequired={false}
              autocomplete='off'
            />
            <TextField
              formik={formik}
              type='text'
              field='lastName'
              placeholder={t('form.labels.lastName')}
              label={t('form.labels.lastName')}
              isRequired={!formik.values.singleName}
              autocomplete='off'
            />
          </div>
          {includeSingleNameField && (
            <CheckBox
              formik={formik}
              field='singleName'
              checked={formik.values.singleName}
              onChange={() => formik.setFieldValue('singleName', !formik.values.singleName)}
              label={t('registration.singleName')}
              square={true}
            />
          )}
          {/* Date of Birth */}
          <div className={styles['form__birth']}>
            <div className={styles['form__select-container']}>
              <div className={styles['form__item-title']}>
                {t('form.labels.dateOfBirth')} <span className={styles['form__required']}>*</span>
              </div>
              <div className={styles['form__selects']}>
                <Select
                  label={t('countdown.time.calendar.month')}
                  formik={formik}
                  options={months()}
                  field='dobMonth'
                  value={formik.values.dobMonth}
                  isRequired={true}
                  ariaLabel={t('registration.dateOfBirthMonth')}
                  isSearchable={true}
                />
                <Select
                  label={t('countdown.time.calendar.day')}
                  formik={formik}
                  options={days}
                  field='dobDay'
                  value={formik.values.dobDay}
                  isRequired={true}
                  ariaLabel={t('registration.dateOfBirthDay')}
                  isSearchable={true}
                />
                <Select
                  label={t('countdown.time.calendar.year')}
                  formik={formik}
                  options={years}
                  field='dobYear'
                  value={formik.values.dobYear}
                  isRequired={true}
                  ariaLabel={t('registration.dateOfBirthYear')}
                  isSearchable={true}
                />
              </div>
            </div>
          </div>

          <div className={styles['hr']}>{t('registration.contactInformation')}</div>

          <div className={styles['form__email-section']}>
            <TextField
              formik={formik}
              type='email'
              field='email'
              placeholder={t('form.labels.email')}
              label={t('form.labels.email')}
              isRequired={true}
              autocomplete='off'
            />
            <TextField
              formik={formik}
              type='email'
              field='confirmEmail'
              placeholder={t('form.labels.confirmEmail')}
              label={t('form.labels.confirmEmail')}
              isRequired={true}
              autocomplete='off'
            />
          </div>
          <div className={styles['form__phone-section']}>
            <PhoneInput formik={formik} field='phone' autocomplete={'off'} />
          </div>

          <div className={styles['hr']}>{t('registration.qualifyingResidentialAddress')}</div>
          <AddressInput formik={formik} styles={styles} autocomplete='off' />

          {enableDifferentMailingAddress ? (
            <>
              <div className={styles['hr']}>{t('registration.mailingAddress')}</div>
              <CheckBox
                formik={formik}
                field='differentMailingAddress'
                checked={formik.values.differentMailingAddress}
                onChange={() => formik.setFieldValue('differentMailingAddress', !formik.values.differentMailingAddress)}
                label={t('registration.differentMailingAddress')}
                ariaLabel={t('registration.differentMailingAddress')}
                square={true}
              />
              {formik.values.differentMailingAddress && (
                <AddressInput
                  formik={formik}
                  styles={styles}
                  addressFieldName='mailingAddress'
                  searchAddressFieldName='searchMailingAddress'
                  addressNotListedFieldName='mailingAddressNotListed'
                  autocomplete='off'
                />
              )}
            </>
          ) : formik.values.votingChannel === 'mail' && mailingAddressRequiredVoteByMail ? (
            <>
              <div className={styles['hr']}>{t('registration.mailingAddress')}</div>
              <MailingAddressInput formik={formik} styles={styles} autocomplete='off' />
            </>
          ) : null}

          {includeKnownIdNumber && (
            <>
              <div className={styles['hr']}>{t('registration.proofOfIdentity')}</div>

              <div className={styles['form__license']}>
                <TextField
                  formik={formik}
                  type='text'
                  field='knownIdNumber'
                  placeholder='00000000'
                  label={t('registration.driversLicenseIfApplicable')}
                  {...knownIdNumberTextFieldConfig}
                  autocomplete='off'
                />
              </div>
            </>
          )}
          <div className={styles['hr']}>{t('admin.electorsDetails.registeredVotingDistricts')}</div>

          <CheckBox
            checked={formik.values.electionConfirmations.eligiblePSB}
            onChange={() => formik.setFieldValue('electionConfirmations.eligiblePSB', !formik.values.electionConfirmations.eligiblePSB)}
            label={t('registration.confirmEligibleVoteInPSB')}
            square={true}
          />
          <CheckBox
            checked={formik.values.electionConfirmations.eligibleCSLF}
            onChange={() => formik.setFieldValue('electionConfirmations.eligibleCSLF', !formik.values.electionConfirmations.eligibleCSLF)}
            label={t('registration.confirmEligibleVoteInCSLF')}
            square={true}
          />
          <FormikError formik={formik} fieldName={'electionConfirmations'} />
          <div className={styles['divider']}></div>

          {(serverError === 'REGISTERED' || serverError === 'INELIGIBLE') && (
            <AlertBanner text={t(`registration.form.errors.${serverError.toLocaleLowerCase()}`)} error={true} />
          )}
          {/* Buttons */}
          <div className={styles['form__actions']}>
            <NVButton variant='primary' text={t('buttons.clearForm')} height={50} onClick={formik.handleReset} />
            <NVButton text={t('buttons.add')} height={50} onClick={formik.handleSubmit} />
          </div>
        </form>
      </div>
    </>
  )
}
